/**
 * The playback levels you set on other people, remembered between visits.
 *
 * A level belongs to the person, not to the connection they happen to be on.
 * Someone who turns their camera off and on again, drops out and reconnects, or
 * comes back tomorrow should sound the way you last set them - having to reach
 * for the slider every time somebody's network hiccups is the whole complaint
 * this answers. So the level is filed under the nickname, and every tile that
 * opens for that nickname starts where you left it.
 *
 * The key is the nickname folded to lower case, which is the identity the server
 * uses too: the nicknames it sends are already in their normalised display form,
 * and its uniqueness key is exactly that form lowercased - see the `nicknameKey`
 * in src/util/nickname.js. So `Alice` and `alice` are one person here as well as
 * there, without this having to repeat the server's normalisation rules.
 *
 * Only levels that differ from the default are kept, and only the most recently
 * set `LIMIT` of them: a busy server should not be able to fill a browser's
 * storage with a thousand entries that all say "normal volume".
 */
const STORAGE_KEY = 'webchat.volumes';

/** How many people's levels to keep. The least recently set are dropped first. */
const LIMIT = 200;

/**
 * `videoHidden` rides along with the level rather than living somewhere of its
 * own: it is the same kind of thing filed under the same key for the same reason.
 * Putting away a camera that keeps reconnecting and having it come back every
 * time is exactly the complaint described above, about volume.
 */
const DEFAULT = { volume: 1, muted: false, videoHidden: false };

/** The identity a level is filed under. */
export function volumeKey(nickname) {
  return String(nickname ?? '').toLocaleLowerCase('en-US');
}

/** True for a level that is just the default, and so not worth storing. */
function isDefault({ volume, muted, videoHidden }) {
  return (
    volume === DEFAULT.volume &&
    muted === DEFAULT.muted &&
    videoHidden === DEFAULT.videoHidden
  );
}

function read() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
    return stored && typeof stored === 'object' ? stored : {};
  } catch {
    return {};
  }
}

let levels = read();

/**
 * The level last set for a nickname, or the default for someone new. Values are
 * clamped on the way out: storage is editable, and a volume outside 0..1 throws
 * when it reaches a media element.
 */
export function recallVolume(nickname) {
  const stored = levels[volumeKey(nickname)];
  if (!stored || typeof stored !== 'object') return { ...DEFAULT };
  const volume = Number(stored.volume);
  return {
    volume: Number.isFinite(volume) ? Math.min(1, Math.max(0, volume)) : DEFAULT.volume,
    muted: stored.muted === true,
    videoHidden: stored.videoHidden === true,
  };
}

/** Files a level under a nickname, or forgets one that is back to the default. */
export function rememberVolume(nickname, { volume, muted, videoHidden }) {
  const key = volumeKey(nickname);
  if (!key) return;
  const level = { volume, muted: muted === true, videoHidden: videoHidden === true };

  if (isDefault(level)) delete levels[key];
  else levels[key] = { ...level, at: Date.now() };

  const keys = Object.keys(levels);
  if (keys.length > LIMIT) {
    const oldestFirst = keys.sort((a, b) => (levels[a].at ?? 0) - (levels[b].at ?? 0));
    for (const stale of oldestFirst.slice(0, keys.length - LIMIT)) delete levels[stale];
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(levels));
  } catch {
    /* storage may be unavailable; the level still applies to this page */
  }
}

export default { recallVolume, rememberVolume, volumeKey };
